import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { readPref, writePref } from '../services/crossShellPrefs';

type Theme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
  setTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

// One key, client-only — no backend user pref. Stored via `crossShellPrefs` rather than
// `localStorage` directly, because localStorage is ORIGIN-scoped and the products now sit
// on four hostnames: choosing dark on brainstorm and finding light on beats was not a
// theme bug, it was the choice being written where the other shells cannot read it.
//
// Still read once, lazily, and `document.cookie` is synchronous like localStorage — so the
// very first paint has the right class and there is no light→dark flash.
const STORAGE_KEY = 'jcseo_theme';

function initialTheme(): Theme {
  const stored = readPref(STORAGE_KEY);
  if (stored === 'light' || stored === 'dark') return stored;
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return 'light';
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(initialTheme);

  // The class lives on <html> (documentElement), not on any page shell, so login/register
  // and every route theme together and Tailwind's `dark:` variants resolve everywhere.
  useEffect(() => {
    // Hover fades (`transition` on cards and buttons) would fade the theme
    // change too, so some parts lagged behind the page. Turn them off for the
    // switch, and back on once the new colours have been drawn.
    const root = document.documentElement;
    root.classList.add('jc-theme-switching');
    root.classList.toggle('dark', theme === 'dark');
    void root.offsetHeight;
    const frame = requestAnimationFrame(() => root.classList.remove('jc-theme-switching'));
    writePref(STORAGE_KEY, theme);
    return () => {
      cancelAnimationFrame(frame);
      root.classList.remove('jc-theme-switching');
    };
  }, [theme]);

  const setTheme = useCallback((next: Theme) => setThemeState(next), []);
  const toggleTheme = useCallback(
    () => setThemeState((t) => (t === 'dark' ? 'light' : 'dark')),
    [],
  );

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
